import type { IEncodingVersionAdapter } from '../../../../core/encodingCompatibility';

/**
 * 旧版本到新版本的适配器（v1 -> v2）
 * 将 { x, y: [y, size?], y2 } 转换为 { x, y, size?, group }
 */
export const dynamicBeeSwarmLegacyAdapter: IEncodingVersionAdapter = {
  version: 'v1-to-v2',

  /**
   * 判断是否为旧版本编码格式
   * @param encoding - 编码对象
   * @returns 是否匹配旧版本格式
   */
  isMatch: (encoding: Record<string, unknown>): boolean => {
    return (
      typeof encoding.y2 === 'string' &&
      Array.isArray(encoding.y) &&
      encoding.group === undefined &&
      encoding.size === undefined
    );
  },

  /**
   * 将旧版本编码转换为新版本格式
   * @param encoding - 旧版本编码对象
   * @returns 新版本编码对象
   */
  transform: (encoding: Record<string, unknown>): Record<string, unknown> => {
    const { x, y, y2, ...rest } = encoding;
    const yArray = Array.isArray(y) ? y : [y];
    const [yValue, sizeValue] = yArray;

    const result: Record<string, unknown> = {
      x,
      y: yValue,
      group: y2,
      ...rest
    };

    if (sizeValue) {
      result.size = sizeValue;
    }

    return result;
  },

  /**
   * 将新版本编码反向转换为旧版本格式
   * @param encoding - 新版本编码对象
   * @returns 旧版本编码对象
   */
  reverse: (encoding: Record<string, unknown>): Record<string, unknown> => {
    const { x, y, size, group, ...rest } = encoding;

    const yArray: string[] = [];
    if (typeof y === 'string') {
      yArray.push(y);
    }
    if (typeof size === 'string') {
      yArray.push(size);
    }

    return {
      x,
      y: yArray,
      y2: group,
      ...rest
    };
  }
};

/**
 * 新版本适配器（v2）
 * 直接通过，无需转换
 */
export const dynamicBeeSwarmCurrentAdapter: IEncodingVersionAdapter = {
  version: 'v2',

  /**
   * 判断是否为新版本编码格式
   * @param encoding - 编码对象
   * @returns 是否匹配新版本格式
   */
  isMatch: (encoding: Record<string, unknown>): boolean => {
    return (
      typeof encoding.group === 'string' &&
      typeof encoding.y === 'string' &&
      !Array.isArray(encoding.y) &&
      encoding.y2 === undefined
    );
  },

  /**
   * 直接返回编码，无需转换
   * @param encoding - 编码对象
   * @returns 原始编码对象
   */
  transform: (encoding: Record<string, unknown>): Record<string, unknown> => {
    return encoding;
  }
};

/**
 * 获取动态蜂群图的所有适配器
 * @returns 适配器数组
 */
export function getDynamicBeeSwarmAdapters(): IEncodingVersionAdapter[] {
  return [dynamicBeeSwarmLegacyAdapter, dynamicBeeSwarmCurrentAdapter];
}
